import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Menu, Tooltip } from 'antd';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
	COMPARISON_MODE,
	COMPARISON_MODE_LABELS,
	ComparisonMode,
	ComparisonState,
	DEFAULT_COMPARISON_STATE,
	formatComparisonRangeLabel,
} from '../../utils/AnalyticsComparisonUtils';
import CustomRangeModal from './CustomRangeModal';

interface Props {
	value: ComparisonState;
	onChange: (next: ComparisonState) => void;
	selectedStart: moment.Moment | null;
	selectedEnd: moment.Moment | null;
	isTimeBased?: boolean;
	loading?: boolean;
	disabled?: boolean;
	disabledReason?: string;
}

const ComparisonModeSelect = ({
	value,
	onChange,
	selectedStart,
	selectedEnd,
	isTimeBased = true,
	loading,
	disabled,
	disabledReason,
}: Props) => {
	const [customModalOpen, setCustomModalOpen] = useState(false);

	const handleSelect = useCallback(
		({ key }: { key: string }) => {
			const nextMode = key as ComparisonMode;
			if (nextMode === COMPARISON_MODE.CUSTOM) {
				setCustomModalOpen(true);
				return;
			}
			onChange({ ...DEFAULT_COMPARISON_STATE, mode: nextMode });
		},
		[onChange]
	);

	const handleCustomApply = useCallback(
		(start: moment.Moment, end: moment.Moment) => {
			onChange({
				mode: COMPARISON_MODE.CUSTOM,
				customStart: start,
				customEnd: end,
			});
			setCustomModalOpen(false);
		},
		[onChange]
	);

	const buttonLabel = useMemo(() => {
		if (value.mode === COMPARISON_MODE.NONE) {
			return 'No comparison';
		}
		if (value.mode === COMPARISON_MODE.PREVIOUS_PERIOD) return 'vs prev. period';
		if (value.mode === COMPARISON_MODE.LAST_YEAR) return 'vs last year';
		if (value.mode === COMPARISON_MODE.CUSTOM) return 'vs custom range';
		return COMPARISON_MODE_LABELS[value.mode];
	}, [value]);

	const buttonTitle = useMemo(() => {
		if (value.mode === COMPARISON_MODE.CUSTOM && value.customStart && value.customEnd) {
			return 'Custom: ' + formatComparisonRangeLabel(value.customStart, value.customEnd);
		}
		return undefined;
	}, [value]);

	const overlay = (
		<Menu onClick={handleSelect} selectedKeys={[value.mode]}>
			<Menu.Item key={COMPARISON_MODE.NONE}>
				{COMPARISON_MODE_LABELS[COMPARISON_MODE.NONE]}
			</Menu.Item>
			<Menu.Item key={COMPARISON_MODE.PREVIOUS_PERIOD}>
				Compare to: {COMPARISON_MODE_LABELS[COMPARISON_MODE.PREVIOUS_PERIOD]}
			</Menu.Item>
			<Menu.Item key={COMPARISON_MODE.LAST_YEAR}>
				Compare to: {COMPARISON_MODE_LABELS[COMPARISON_MODE.LAST_YEAR]}
			</Menu.Item>
			<Menu.Item key={COMPARISON_MODE.CUSTOM}>
				Compare to: {COMPARISON_MODE_LABELS[COMPARISON_MODE.CUSTOM]}…
			</Menu.Item>
		</Menu>
	);

	const isDisabled = loading || disabled;

	const button = (
		<Button
			className="footnoteMedium max-w-[160px]"
			disabled={isDisabled}
			title={!loading && disabled && disabledReason ? undefined : buttonTitle}
		>
			<span className="inline-block max-w-[120px] truncate align-middle">{buttonLabel}</span>
			{loading ? (
				<LoadingOutlined className="ml-1" spin />
			) : (
				<DownOutlined className="ml-1" />
			)}
		</Button>
	);

	const trigger =
		!loading && disabled && disabledReason ? (
			<Tooltip title={disabledReason}>
				<span className="inline-block">{button}</span>
			</Tooltip>
		) : (
			button
		);

	return (
		<>
			<Dropdown overlay={overlay} trigger={['click']} disabled={isDisabled}>
				{trigger}
			</Dropdown>
			<CustomRangeModal
				open={customModalOpen}
				onClose={() => setCustomModalOpen(false)}
				onApply={handleCustomApply}
				selectedStart={selectedStart}
				selectedEnd={selectedEnd}
				isTimeBased={isTimeBased}
				initialStart={value.customStart || null}
				initialEnd={value.customEnd || null}
			/>
		</>
	);
};

export default ComparisonModeSelect;
